import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  bulkUpdateEventActivitySelection,
  createEventActivities,
  copyEventActivitySelections,
  CreateEventActivitiesPayload,
  deleteEventActivity,
  deleteEventParticipant,
  exportEventActivityOverrides,
  importEventFromPreviousEvent,
  resetEventSelections,
  UpdateEventActivityPayload,
  updateEventActivity,
  updateEventActivityOrder,
  updateEventParticipantSelection,
  updateEventParticipantStatus,
  updateTeamEvent,
} from 'app/frontend/base/api/event'
import { AddEventFormValues } from '../../TeamEventOverview/components/AddEventDialog'
import useOptimisticEventUpdate from '../hooks/useOptimisticEventUpdate'

type Props = {
  teamId: number
  eventId?: number
  applyEventUpdate: (event: wowaudit.Event) => void
  optimisticEventUpdate: ReturnType<typeof useOptimisticEventUpdate>
  onError: (error: unknown) => void
}

const useTeamEventMutations = ({
  teamId,
  eventId,
  applyEventUpdate,
  optimisticEventUpdate,
  onError,
}: Props) => {
  const queryClient = useQueryClient()
  const updateEventMutation = useMutation({
    mutationFn: async (
      values: AddEventFormValues | Partial<wowaudit.Event>,
    ) => {
      if (!eventId) throw new Error('Missing event id')
      return await updateTeamEvent(
        teamId,
        eventId,
        values as Partial<wowaudit.Event>,
      )
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({
      eventParticipantId,
      status,
    }: {
      eventParticipantId: number
      status: wowaudit.EventParticipantReference['status']
    }) => updateEventParticipantStatus(teamId, eventParticipantId, status),
    onMutate: async (variables) =>
      await optimisticEventUpdate.optimisticStatusUpdate(variables),
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError: (error, _variables, context) => {
      optimisticEventUpdate.rollback(context)
      onError(error)
    },
  })

  const updateSelectionMutation = useMutation({
    mutationFn: ({
      eventParticipantId,
      role,
      specId,
      characterId,
      selected,
      activityId,
    }: {
      eventParticipantId: number
      role?: string
      specId?: number | null
      characterId?: number
      selected?: boolean
      activityId?: number
    }) =>
      updateEventParticipantSelection(teamId, eventParticipantId, {
        activityId,
        role,
        specId,
        characterId,
        selected,
      }),
    onMutate: async (variables) =>
      await optimisticEventUpdate.optimisticSelectionUpdate(variables),
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError: (error, _variables, context) => {
      optimisticEventUpdate.rollback(context)
      onError(error)
    },
  })

  const removeGuestMutation = useMutation({
    mutationFn: (eventParticipantId: number) =>
      deleteEventParticipant(teamId, eventParticipantId),
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  const updateActivitySelectionForAllParticipantsMutation = useMutation({
    mutationFn: ({
      activityId,
      selected,
    }: {
      activityId: number
      selected: boolean
    }) => {
      if (!eventId) throw new Error('Missing event id')
      return bulkUpdateEventActivitySelection(
        teamId,
        eventId,
        activityId,
        selected,
      )
    },
    onMutate: async (variables) =>
      await optimisticEventUpdate.optimisticActivitySelectionUpdate(variables),
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError: (error, _variables, context) => {
      optimisticEventUpdate.rollback(context)
      onError(error)
    },
  })

  const updateEventActivityMutation = useMutation({
    mutationFn: ({
      activityId,
      eventActivity,
    }: {
      activityId: number
      eventActivity: UpdateEventActivityPayload
    }) => {
      if (!eventId) throw new Error('Missing event id')
      return updateEventActivity(teamId, eventId, activityId, eventActivity)
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError: (error) => {
      onError(error)
    },
  })

  const updateEventActivityOrderMutation = useMutation({
    mutationFn: ({ activityOrder }: { activityOrder: number[] }) => {
      if (!eventId) throw new Error('Missing event id')
      return updateEventActivityOrder(teamId, eventId, activityOrder)
    },
    onMutate: async (variables) =>
      await optimisticEventUpdate.optimisticActivityOrderUpdate(variables),
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError: (error, _variables, context) => {
      optimisticEventUpdate.rollback(context)
      onError(error)
    },
  })

  const copyEventActivitySelectionsMutation = useMutation({
    mutationFn: ({
      activityId,
      sourceActivityId,
      sourceEventId,
    }: {
      activityId: number
      sourceActivityId: number
      sourceEventId?: number
    }) => {
      if (!eventId) throw new Error('Missing event id')
      return copyEventActivitySelections(
        teamId,
        eventId,
        activityId,
        sourceActivityId,
        sourceEventId,
      )
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  const exportEventActivityOverridesMutation = useMutation({
    mutationFn: ({ activityId }: { activityId: number }) => {
      if (!eventId) throw new Error('Missing event id')
      return exportEventActivityOverrides(teamId, eventId, activityId)
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
      void queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
    },
    onError,
  })

  const createEventActivitiesMutation = useMutation({
    mutationFn: ({
      eventActivity,
    }: {
      eventActivity: CreateEventActivitiesPayload
    }) => {
      if (!eventId) throw new Error('Missing event id')
      return createEventActivities(teamId, eventId, eventActivity)
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  const deleteEventActivityMutation = useMutation({
    mutationFn: ({ activityId }: { activityId: number }) => {
      if (!eventId) throw new Error('Missing event id')
      return deleteEventActivity(teamId, eventId, activityId)
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  const importEventFromPreviousEventMutation = useMutation({
    mutationFn: ({ sourceEventId }: { sourceEventId: number }) => {
      if (!eventId) throw new Error('Missing event id')
      return importEventFromPreviousEvent(teamId, eventId, sourceEventId)
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  const resetEventSelectionsMutation = useMutation({
    mutationFn: () => {
      if (!eventId) throw new Error('Missing event id')
      return resetEventSelections(teamId, eventId)
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
    },
    onError,
  })

  return {
    updateEventMutation,
    updateStatusMutation,
    updateSelectionMutation,
    removeGuestMutation,
    updateActivitySelectionForAllParticipantsMutation,
    updateEventActivityMutation,
    updateEventActivityOrderMutation,
    copyEventActivitySelectionsMutation,
    exportEventActivityOverridesMutation,
    createEventActivitiesMutation,
    deleteEventActivityMutation,
    importEventFromPreviousEventMutation,
    resetEventSelectionsMutation,
  }
}

export default useTeamEventMutations
